export type TDevToolsPanel = 'console' | 'elements' | 'network' | 'sources';

export interface IDevToolsFrontendOptions {
  iframe: HTMLIFrameElement;
  entrypointUrl: string;
  /** Resolves when the authenticated transport accepts a command, before execution. */
  send(message: string): Promise<void>;
  onClose(reason: string): void;
}

/** Real Chrome DevTools connected through an origin-bound, backpressured MessagePort. */
export class DevToolsFrontend {
  private readonly nonce = crypto.randomUUID();
  private readonly origin: string;
  private readonly url: URL;
  private port?: MessagePort;
  private started = false;
  private closed = false;
  private ready = false;
  private sequence = 0;
  private commandRunning = false;
  private timer?: ReturnType<typeof setTimeout>;
  private connected?: { resolve(): void; reject(error: Error): void };
  private pendingEvent?: { sequence: number; message: string; resolve(): void; reject(error: Error): void;
    timer: ReturnType<typeof setTimeout> };

  constructor(private options: IDevToolsFrontendOptions) {
    this.origin = options.iframe.ownerDocument.location.origin;
    this.url = new URL(options.entrypointUrl, options.iframe.ownerDocument.location.href);
    if (this.url.origin !== this.origin || !['http:', 'https:'].includes(this.url.protocol)
      || this.url.username || this.url.password) throw new Error('DevTools requires same-origin assets.');
    this.url.hash = new URLSearchParams({ nonce: this.nonce }).toString();
  }

  public start(): Promise<void> {
    if (this.started || this.closed) return Promise.reject(new Error('DevTools frontend has already started.'));
    this.started = true;
    const promise = new Promise<void>((resolve, reject) => { this.connected = { resolve, reject }; });
    this.options.iframe.ownerDocument.defaultView!.addEventListener('message', this.bind);
    this.timer = setTimeout(() => this.fail('DevTools frontend connection timed out.'), 20000);
    this.options.iframe.title = 'Browser DevTools';
    this.options.iframe.src = this.url.href;
    return promise;
  }

  private bind = (event: MessageEvent): void => {
    if (this.closed || this.port || event.origin !== this.origin
      || event.source !== this.options.iframe.contentWindow
      || event.data?.type !== 'smartbrowser-devtools-ready' || event.data.nonce !== this.nonce) return;
    const channel = new MessageChannel();
    this.port = channel.port1;
    this.port.onmessage = event => { void this.message(event.data).catch(() => this.fail('DevTools transport failed.')); };
    this.port.onmessageerror = () => this.fail('Invalid DevTools transport message.');
    this.port.start();
    this.options.iframe.contentWindow!.postMessage({ type: 'smartbrowser-devtools-bind', nonce: this.nonce },
      this.origin, [channel.port2]);
    if (this.pendingEvent) this.postEvent(this.pendingEvent);
  };

  private async message(data: unknown): Promise<void> {
    if (this.closed) return;
    if (!data || typeof data !== 'object') throw new Error('Invalid DevTools message.');
    const event = data as { type?: string; message?: unknown; sequence?: unknown; reason?: unknown };
    if (event.type === 'connected' && !this.ready) {
      this.ready = true;
      clearTimeout(this.timer);
      this.connected!.resolve();
      this.connected = undefined;
    } else if (event.type === 'command' && !this.commandRunning && typeof event.message === 'string'
      && new TextEncoder().encode(event.message).byteLength <= 1048576 && Number.isSafeInteger(event.sequence)) {
      this.commandRunning = true;
      try {
        await this.options.send(event.message);
        if (!this.closed) this.port!.postMessage({ type: 'ack', sequence: event.sequence });
      } finally { this.commandRunning = false; }
    } else if (event.type === 'ack' && event.sequence === this.pendingEvent?.sequence) {
      const pending = this.pendingEvent!;
      this.pendingEvent = undefined;
      clearTimeout(pending.timer);
      pending.resolve();
    } else if (event.type === 'closed') {
      this.fail(typeof event.reason === 'string' ? event.reason.slice(0, 512) : 'DevTools closed.');
    } else throw new Error('Invalid DevTools message.');
  }

  /** Await before dispatching the next CDP event/response; this preserves stream backpressure. */
  public dispatch(message: string): Promise<void> {
    if (!this.started || this.closed || this.pendingEvent) return Promise.reject(new Error('DevTools cannot accept an event.'));
    if (typeof message !== 'string' || new TextEncoder().encode(message).byteLength > 8388608) {
      return Promise.reject(new Error('DevTools message exceeds the size limit.'));
    }
    return new Promise<void>((resolve, reject) => {
      const sequence = ++this.sequence;
      const timer = setTimeout(() => this.fail('DevTools frontend stopped receiving messages.'), 15000);
      this.pendingEvent = { sequence, message, resolve, reject, timer };
      if (this.port) this.postEvent(this.pendingEvent);
    });
  }

  private postEvent(event: { sequence: number; message: string }): void {
    this.port!.postMessage({ type: 'event', sequence: event.sequence, message: event.message });
  }

  public showPanel(panel: TDevToolsPanel): void {
    if (!this.ready || this.closed) throw new Error('DevTools is not connected.');
    this.port!.postMessage({ type: 'panel', panel });
  }

  private fail(reason: string): void {
    if (this.closed) return;
    this.close();
    this.options.onClose(reason);
  }

  public close(): void {
    if (this.closed) return;
    this.closed = true;
    clearTimeout(this.timer);
    this.options.iframe.ownerDocument.defaultView?.removeEventListener('message', this.bind);
    const error = new Error('DevTools frontend closed.');
    this.connected?.reject(error);
    this.connected = undefined;
    if (this.pendingEvent) {
      clearTimeout(this.pendingEvent.timer);
      this.pendingEvent.reject(error);
      this.pendingEvent = undefined;
    }
    this.port?.postMessage({ type: 'close' });
    this.port?.close();
    this.port = undefined;
    this.options.iframe.src = 'about:blank';
  }
}
